/**
 * PayslipTable Component
 * 
 * Main table component for displaying office employee payslip details.
 * Renders the complete payslip HTML table with all sections:
 * - Header (組織コード, 社員コード, 氏名, title, subtitle)
 * - 所属 (Department)
 * - 支給 (Payment section)
 * - 控除 (Deduction section)
 * - 勤怠 (Attendance section)
 * - 差引 (Deduction amount section) - different for salary vs bonus
 * - Company name (optional)
 * - メモ (Memo section) with cumulative totals
 * 
 * Features:
 * - Supports both salary (給与) and bonus (賞与) payslip types
 * - Dynamic rendering based on enabled fields from settings
 * - Responsive to settings (colors, fonts, field visibility)
 * - Different table structures for salary vs bonus (especially 支給 and 差引 sections)
 * - Safe array access for older browser compatibility
 * - Empty cell handling to maintain table structure
 * 
 * @component
 */

import React from 'react';
import { getAllDataMappings, formatPayslipValue } from './DataMappingUtils';
import { getDefaultSettings } from './SettingsUtils';

/**
 * Type definitions for payslip data structures.
 * These types are defined locally since they don't exist in EmployeeService.
 */

/**
 * Payment data interface (for salary payslips).
 * @interface
 */
interface PaymentData {
  [key: string]: any;
}

interface DeductionData {
  [key: string]: any;
}

interface AttendanceData {
  [key: string]: any;
}

interface BonusPaymentData {
  [key: string]: any;
}

interface BonusDeductionData {
  [key: string]: any;
}

interface BonusAttendanceData {
  [key: string]: any;
}

interface SalaryNotesData {
  [key: string]: any;
}

interface BonusNotesData {
  [key: string]: any;
}

/**
 * Props interface for PayslipTable component.
 */
interface PayslipTableProps {
  dataType: 'salary' | 'bonus';                    // Payslip type: salary or bonus
  selectedYear: string;                            // Selected year
  selectedMonth: string;                           // Selected month (for salary)
  selectedPeriod?: string;                         // Selected period (for bonus)
  empCode: string;                                 // Employee code
  employeeName: string;                            // Employee name
  hasSearched: boolean;                            // Whether a search has been performed
  paymentData: PaymentData | null;                 // Salary payment data
  deductionData: DeductionData | null;             // Salary deduction data
  attendanceData: AttendanceData | null;           // Salary attendance data
  bonusPaymentData: BonusPaymentData | null;       // Bonus payment data
  bonusDeductionData: BonusDeductionData | null;   // Bonus deduction data
  bonusAttendanceData: BonusAttendanceData | null; // Bonus attendance data
  salaryNotesData: SalaryNotesData | null;         // Salary notes data
  bonusNotesData: BonusNotesData | null;           // Bonus notes data
  payslipSettings: {
    borderColor: string;
    backgroundColor: string;
    fontSize: string;
    fontFamily: string;
    showCompanyName: boolean;
    showEmployeeCode: boolean;
    showMemo: boolean;
    payment: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    deduction: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    attendance: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    notes: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
  };
  tableData: {
    memo1: string;
    memo2: string;
  };
  paymentItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  deductionItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  attendanceItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  notesItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  getPeriodDisplayName?: (period: string) => string; // Optional function to display period names
}

/**
 * PayslipTable Component
 * 
 * Renders the complete payslip table structure with all sections.
 * Handles both salary and bonus payslip types with different layouts.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const PayslipTable: React.FC<PayslipTableProps> = ({
  dataType,
  selectedYear,
  selectedMonth,
  selectedPeriod,
  empCode,
  employeeName,
  hasSearched,
  paymentData,
  deductionData,
  attendanceData,
  bonusPaymentData,
  bonusDeductionData,
  bonusAttendanceData,
  salaryNotesData,
  bonusNotesData,
  payslipSettings,
  tableData,
  paymentItems,
  deductionItems,
  attendanceItems,
  notesItems,
  getPeriodDisplayName
}) => {
  
  /**
   * Get all data mappings for easy lookup.
   * 
   * Consolidates all payslip data (payment, deduction, attendance, notes)
   * into a single object for efficient value retrieval by cell ID.
   */
  const dataMappings = getAllDataMappings(
    dataType,
    paymentData,
    deductionData,
    attendanceData,
    bonusPaymentData,
    bonusDeductionData,
    bonusAttendanceData,
    salaryNotesData,
    bonusNotesData
  );

  /**
   * Safe array includes check - compatible with older browsers.
   * 
   * Provides a fallback for Array.includes() which may not be available
   * on older smartphones. Uses indexOf() as a fallback.
   * 
   * @param arr - Array to check (may be null/undefined)
   * @param item - Item to search for
   * @returns True if array includes item, false otherwise
   */
  const safeIncludes = (arr: any[] | null | undefined, item: any): boolean => {
    if (!arr || !Array.isArray(arr)) {
      return false;
    }
    try {
      // Use includes if available (modern browsers)
      if (typeof arr.includes === 'function') {
        return arr.includes(item);
      }
      // Fallback to indexOf for older browsers (e.g., older Android phones)
      return arr.indexOf(item) !== -1;
    } catch (e) {
      return false;
    }
  };

  /**
   * Ensure payslipSettings is always valid with safe defaults.
   * 
   * Memoized to prevent unnecessary recalculations.
   * Provides default values for all settings properties to prevent errors
   * on older smartphones that may not handle null/undefined gracefully.
   * 
   * @returns Valid PayslipSettings object with all properties defined
   */
  const safeSettings = React.useMemo(() => {
    if (!payslipSettings) {
      return getDefaultSettings(dataType);
    }
    // Ensure all nested properties exist
    return {
      borderColor: payslipSettings.borderColor || '#000000',
      backgroundColor: payslipSettings.backgroundColor || '#ffffff',
      fontSize: payslipSettings.fontSize || '10px',
      fontFamily: payslipSettings.fontFamily || 'Yu Gothic',
      showCompanyName: payslipSettings.showCompanyName !== undefined ? payslipSettings.showCompanyName : true,
      showEmployeeCode: payslipSettings.showEmployeeCode !== undefined ? payslipSettings.showEmployeeCode : true,
      showMemo: payslipSettings.showMemo !== undefined ? payslipSettings.showMemo : true,
      // Ensure all field settings have valid enabled arrays
      payment: payslipSettings.payment && Array.isArray(payslipSettings.payment.enabled) 
        ? payslipSettings.payment 
        : { enabled: [], lastUpdated: '', updatedBy: '' },
      deduction: payslipSettings.deduction && Array.isArray(payslipSettings.deduction.enabled)
        ? payslipSettings.deduction
        : { enabled: [], lastUpdated: '', updatedBy: '' },
      attendance: payslipSettings.attendance && Array.isArray(payslipSettings.attendance.enabled)
        ? payslipSettings.attendance
        : { enabled: [], lastUpdated: '', updatedBy: '' },
      notes: payslipSettings.notes && Array.isArray(payslipSettings.notes.enabled)
        ? payslipSettings.notes
        : { enabled: [], lastUpdated: '', updatedBy: '' }
    };
  }, [payslipSettings, dataType]);

  /**
   * Helper function to get value for a specific valueId.
   * 
   * Retrieves the formatted value for a payslip cell from dataMappings.
   * Returns non-breaking space (\u00A0) if value is empty to maintain row height.
   * 
   * @param valueId - Cell ID (e.g., 'p_v1', 'd_v1', 'a_v1')
   * @returns Formatted value string or non-breaking space if empty
   */
  const getValueForCell = (valueId: string): string => {
    try {
      const value = dataMappings[valueId];
      const formattedValue = formatPayslipValue(value);
      // Return non-breaking space if value is empty to maintain row height
      return formattedValue === '' ? '\u00A0' : formattedValue;
    } catch (e) {
      return '\u00A0'; // Return non-breaking space to maintain row height
    }
  };
  
  /**
   * Dynamic title based on data type.
   * Shows '給与明細書' for salary or '賞与明細書' for bonus.
   */
  const title = dataType === 'salary' ? '給与明細書' : '賞与明細書';
  
  /**
   * Dynamic subtitle based on data type.
   * For salary: "{year}年{month}月分"
   * For bonus: "{year}年{period}分" — period is often "7月" so avoid "月月分"
   */
  let subTitle: string;
  if (dataType === 'salary') {
    subTitle = `${selectedYear}年${selectedMonth}月分`;
  } else {
    // For bonus, selectedPeriod may already end with 月 (e.g. "7月") — avoid "月月分"
    const period = selectedPeriod ?? '';
    subTitle = period.endsWith('月') ? `${selectedYear}年${period}分` : `${selectedYear}年${period}月分`;
  }

  return (
    <div className="table-area">
      <div className="payslip-container">
        <table 
          className="main-table"
          style={{
            fontFamily: safeSettings.fontFamily,
            fontSize: safeSettings.fontSize
          }}
        >
          <style>
            {`
              .main-table td,
              .main-table th {
                font-size: ${safeSettings.fontSize} !important;
              }
              .title {
                font-size: calc(${safeSettings.fontSize} * 1.4) !important;
                font-weight: bold !important;
              }
              .sub-title {
                font-size: calc(${safeSettings.fontSize} * 1.2) !important;
              }
              .company-name {
                font-size: calc(${safeSettings.fontSize} * 2) !important;
                font-weight: bold !important;
              }
              .vertical-text {
                font-size: ${safeSettings.fontSize} !important;
              }
              .memo {
                font-size: ${safeSettings.fontSize} !important;
              }
            `}
          </style>
          <thead>
            {/* Header Section */}
            <tr>
              <td colSpan={2} className="border-rect">組織コード</td>
              {safeSettings.showEmployeeCode && <td className="border-rect">社員コード</td>}
              <td className="border-rect">氏 名</td>
              <td colSpan={safeSettings.showEmployeeCode ? 2 : 3} className="no-border title-cell">
                <span className="title">{title}</span>
              </td>
            </tr>
            <tr>
              <td colSpan={2} className="border-rect white-bg"></td>
              {safeSettings.showEmployeeCode && <td className="border-rect">{hasSearched && employeeName ? empCode : ''}</td>}
              <td className="border-rect">{hasSearched && employeeName ? `${employeeName}様` : '様'}</td>
              <td colSpan={safeSettings.showEmployeeCode ? 2 : 3} className="no-border sub-title-cell">
                <span className="sub-title">{subTitle}</span>
              </td>
            </tr>
          </thead>
          <tbody>
            {/* 所属 Row */}
            <tr className="honsha-row">
              <td colSpan={10} style={{ textAlign: "right" }}>
                {hasSearched ? (
                  dataType === 'salary' 
                    ? (paymentData?.department || '所属')
                    : (bonusPaymentData?.department || '所属')
                ) : '所属'}
              </td>
            </tr>

            {/* 支給 (Payment) Section */}
            {(() => {
              try {
                /**
                 * Safely get enabled payment items array.
                 * Ensures we have a valid array even if settings are corrupted or missing.
                 */
                const paymentEnabled = safeSettings.payment && Array.isArray(safeSettings.payment.enabled) 
                  ? safeSettings.payment.enabled 
                  : [];
                
                /**
                 * Filter selected payment items and maintain order.
                 * Only includes items that are enabled in settings.
                 */
                const selectedPaymentItems = paymentItems.filter(item => 
                  safeIncludes(paymentEnabled, item.id)
                );

              /**
               * For bonus payslip, create dynamic structure based on available items.
               * 
               * Bonus payslip has a different structure than salary:
               * - Regular payment items are arranged in dynamic rows (9 items per row)
               * - Special items (課税支給額, 非課支給額) are moved to a separate total row
               * - RowSpan is calculated dynamically based on number of items
               */
              if (dataType === 'bonus') {
                /**
                 * Filter out 課税支給額 and 非課支給額 from regular rows.
                 * These special items will be displayed in a separate total row.
                 */
                const regularPaymentItems = selectedPaymentItems.filter(item => 
                  !safeIncludes(['bonus_payment_6', 'bonus_payment_7'], item.id)
                );
                
                /**
                 * Calculate how many rows we need (1 row per 9 items, minimum 1 row).
                 * Each row can display 9 payment items (key row + value row = 2 rows per item set).
                 */
                const totalRows = Math.max(1, Math.ceil(regularPaymentItems.length / 9));
                const rows = [];
                
                /**
                 * Calculate total rowSpan for the "支給" vertical label cell.
                 * RowSpan includes: payment rows (key + value pairs) + total rows (key + value).
                 */
                const totalRowSpan = (totalRows * 2) + 2;
                
                for (let i = 0; i < totalRows; i++) {
                  const startIndex = i * 9;
                  const endIndex = startIndex + 9;
                  const rowItems = regularPaymentItems.slice(startIndex, endIndex);
                  
                  if (rowItems.length === 0) break; // Don't create empty rows
                  
                  const keyRow = (
                    <tr key={`payment-key-${i + 1}`} className={i === 0 ? "green-row shikyuu-border-top" : ""}>
                      {i === 0 && <td rowSpan={totalRowSpan} className="vertical-text shikyuu-border-left">支給</td>}
                      {rowItems.map((item, index) => (
                        <td 
                          key={item.keyId}
                          id={item.keyId}
                          className={index === 0 ? "shikyuu-border-left" : index === rowItems.length - 1 ? "shikyuu-border-right" : ""}
                        >
                          {item.name}
                        </td>
                      ))}
                      {Array.from({ length: 9 - rowItems.length }, (_, index) => (
                        <td key={`empty-key-${i + 1}-${index}`} className={index === (9 - rowItems.length - 1) ? "shikyuu-border-right" : ""}>&nbsp;</td>
                      ))}
                    </tr>
                  );
                  
                  const valueRow = (
                    <tr key={`payment-value-${i + 1}`} className={i === totalRows - 1 ? "white-row shikyuu-border-bottom" : "white-row"}>
                      {rowItems.map((item, index) => (
                        <td 
                          key={item.valueId}
                          id={item.valueId}
                          className={index === 0 ? "shikyuu-border-left" : index === rowItems.length - 1 ? "shikyuu-border-right" : ""}
                        >
                          {hasSearched ? getValueForCell(item.valueId) : ''}
                        </td>
                      ))}
                      {Array.from({ length: 9 - rowItems.length }, (_, index) => (
                        <td key={`empty-value-${i + 1}-${index}`} className={index === (9 - rowItems.length - 1) ? "shikyuu-border-right" : ""}>&nbsp;</td>
                      ))}
                    </tr>
                  );
                  
                  rows.push(keyRow, valueRow);
                }
                
                /**
                 * Add total row for bonus - includes 課税支給額, 非課支給額, and 支給合計.
                 * These are special items that are displayed separately in a total row.
                 */
                const taxablePaymentItem = selectedPaymentItems.find(item => item.id === 'bonus_payment_6');
                const nonTaxablePaymentItem = selectedPaymentItems.find(item => item.id === 'bonus_payment_7');
                
                const totalRow = (
                  <tr key="payment-total">
                    <td className="shikyuu-border-left"></td>
                    {Array.from({ length: 5 }, (_, index) => (
                      <td key={`empty-total-${index}`}></td>
                    ))}
                    {taxablePaymentItem && (
                      <td className="shikyuu-border-right shikyuu-special-border-top shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id={taxablePaymentItem.keyId}>
                        {taxablePaymentItem.name}
                      </td>
                    )}
                    {nonTaxablePaymentItem && (
                      <td className="shikyuu-border-right shikyuu-special-border-top shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id={nonTaxablePaymentItem.keyId}>
                        {nonTaxablePaymentItem.name}
                      </td>
                    )}
                    <td className="shikyuu-border-right shikyuu-special-border-top shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id='p_k33'>支給合計</td>
                  </tr>
                );
                
                const totalValueRow = (
                  <tr key="payment-total-value" className="shikyuu-border-bottom">
                    <td className="shikyuu-border-left"></td>
                    {Array.from({ length: 5 }, (_, index) => (
                      <td key={`empty-total-value-${index}`}></td>
                    ))}
                    {taxablePaymentItem && (
                      <td className="shikyuu-border-right shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id={taxablePaymentItem.valueId}>
                        {hasSearched ? getValueForCell(taxablePaymentItem.valueId) : ''}
                      </td>
                    )}
                    {nonTaxablePaymentItem && (
                      <td className="shikyuu-border-right shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id={nonTaxablePaymentItem.valueId}>
                        {hasSearched ? getValueForCell(nonTaxablePaymentItem.valueId) : ''}
                      </td>
                    )}
                    <td className="shikyuu-border-right shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id='p_v33'>
                      {hasSearched ? getValueForCell('p_v33') : ''}
                    </td>
                  </tr>
                );
                
                rows.push(totalRow, totalValueRow);
                return rows;
              }

              /**
               * For salary payslip, use the original fixed structure.
               * 
               * Salary payslip has a fixed structure with 10 rows:
               * - 5 key-value pairs (items 1-9, 10-18, 19-27, 28-29)
               * - 2 total rows (key + value) with 課税支給額, 非課支給額, and 支給合計
               */
              const rows = [];
              
              // First row (items 1-9) - displays first 9 payment items
              const firstRowItems = selectedPaymentItems.slice(0, 9);
              const firstKeyRow = (
                <tr key="payment-key-1" className="green-row shikyuu-border-top">
                  <td rowSpan={10} className="vertical-text shikyuu-border-left">支給</td>
                  {firstRowItems.map((item, index) => (
                    <td 
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "shikyuu-border-left" : index === 8 ? "shikyuu-border-right" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {Array.from({ length: 9 - firstRowItems.length }, (_, index) => (
                    <td key={`empty-key-1-${index}`} className={index === (9 - firstRowItems.length - 1) ? "shikyuu-border-right" : ""}></td>
                  ))}
                </tr>
              );
              
              const firstValueRow = (
                <tr key="payment-value-1" className="white-row">
                  {firstRowItems.map((item, index) => (
                    <td 
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "shikyuu-border-left" : index === 8 ? "shikyuu-border-right" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {Array.from({ length: 9 - firstRowItems.length }, (_, index) => (
                    <td key={`empty-value-1-${index}`} className={index === (9 - firstRowItems.length - 1) ? "shikyuu-border-right" : ""}></td>
                  ))}
                </tr>
              );
              
              // Second row (items 10-18)
              const secondRowItems = selectedPaymentItems.slice(9, 18);
              const secondKeyRow = (
                <tr key="payment-key-2">
                  {secondRowItems.map((item, index) => (
                    <td 
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "shikyuu-border-left" : index === 8 ? "shikyuu-border-right" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {Array.from({ length: 9 - secondRowItems.length }, (_, index) => (
                    <td key={`empty-key-2-${index}`} className={index === (9 - secondRowItems.length - 1) ? "shikyuu-border-right" : ""}></td>
                  ))}
                </tr>
              );
              
              const secondValueRow = (
                <tr key="payment-value-2">
                  {secondRowItems.map((item, index) => (
                    <td 
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "shikyuu-border-left" : index === 8 ? "shikyuu-border-right" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {Array.from({ length: 9 - secondRowItems.length }, (_, index) => (
                    <td key={`empty-value-2-${index}`} className={index === (9 - secondRowItems.length - 1) ? "shikyuu-border-right" : ""}></td>
                  ))}
                </tr>
              );
              
              // Third row (items 19-27)
              const thirdRowItems = selectedPaymentItems.slice(18, 27);
              const thirdKeyRow = (
                <tr key="payment-key-3">
                  {thirdRowItems.map((item, index) => (
                    <td 
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "shikyuu-border-left" : index === 8 ? "shikyuu-border-right" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {Array.from({ length: 9 - thirdRowItems.length }, (_, index) => (
                    <td key={`empty-key-3-${index}`} className={index === (9 - thirdRowItems.length - 1) ? "shikyuu-border-right" : ""}></td>
                  ))}
                </tr>
              );
              
              const thirdValueRow = (
                <tr key="payment-value-3">
                  {thirdRowItems.map((item, index) => (
                    <td 
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "shikyuu-border-left" : index === 8 ? "shikyuu-border-right" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {Array.from({ length: 9 - thirdRowItems.length }, (_, index) => (
                    <td key={`empty-value-3-${index}`} className={index === (9 - thirdRowItems.length - 1) ? "shikyuu-border-right" : ""}></td>
                  ))}
                </tr>
              );
              
              /**
               * Fourth row - Different handling for salary vs bonus.
               * 
               * Salary: items 28-29 (非課支給額 and 課税支給額 are removed from regular rows)
               * Bonus: items 28-31 (all items are in regular rows, special items in total row)
               */
              let fourthRowItems;
              if (dataType === 'salary') {
                // For salary: items 28-29 (removed 非課支給額 and 課税支給額 from regular rows)
                fourthRowItems = selectedPaymentItems.slice(27, 29);
              } else {
                // For bonus: items 28-31 (include all items in regular rows, special items in total row)
                fourthRowItems = selectedPaymentItems.slice(27, 31);
              }
              const fourthKeyRow = (
                <tr key="payment-key-4">
                  {fourthRowItems.map((item, index) => (
                    <td 
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "shikyuu-border-left" : index === 8 ? "shikyuu-border-right" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {Array.from({ length: 9 - fourthRowItems.length }, (_, index) => (
                    <td key={`empty-key-4-${index}`} className={index === (9 - fourthRowItems.length - 1) ? "shikyuu-border-right" : ""}></td>
                  ))}
                </tr>
              );
              
              const fourthValueRow = (
                <tr key="payment-value-4">
                  {fourthRowItems.map((item, index) => (
                    <td 
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "shikyuu-border-left" : index === 8 ? "shikyuu-border-right" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {Array.from({ length: 9 - fourthRowItems.length }, (_, index) => (
                    <td key={`empty-value-4-${index}`} className={index === (9 - fourthRowItems.length - 1) ? "shikyuu-border-right" : ""}></td>
                  ))}
                </tr>
              );
              
              /**
               * Total rows - Different layout for salary vs bonus.
               * 
               * Salary: 課税支給額, 非課支給額, and 支給合計 are in the total row.
               * Bonus: Only 支給合計 is in the total row (special items are in a separate row above).
               */
              let taxablePaymentItem, nonTaxablePaymentItem;
              
              if (dataType === 'salary') {
                // For salary: 課税支給額 at position 6, 非課支給額 at position 7, 支給合計 at position 8
                taxablePaymentItem = selectedPaymentItems.find(item => item.id === 'taxable_payment');
                nonTaxablePaymentItem = selectedPaymentItems.find(item => item.id === 'non_taxable_payment');
              } else {
                /**
                 * For bonus: No special items in total row (they're already in separate row above).
                 * 支給合計 is at position 8.
                 * Special items (課税支給額 and 非課支給額) are handled separately above.
                 */
                taxablePaymentItem = null;
                nonTaxablePaymentItem = null;
              }
              
              const totalRow = (
                <tr key="payment-total" className="shikyuu-border-bottom">
                  <td></td>
                  {dataType === 'salary' ? (
                    // Salary mode: 5 empty cells + 課税支給額 + 非課支給額 + 支給合計
                    <>
                      {Array.from({ length: 5 }, (_, index) => (
                        <td key={`empty-total-${index}`}></td>
                      ))}
                      {taxablePaymentItem && (
                        <td className="shikyuu-border-right shikyuu-special-border-top shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id={taxablePaymentItem.keyId}>
                          {taxablePaymentItem.name}
                        </td>
                      )}
                      {nonTaxablePaymentItem && (
                        <td className="shikyuu-border-right shikyuu-special-border-top shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id={nonTaxablePaymentItem.keyId}>
                          {nonTaxablePaymentItem.name}
                        </td>
                      )}
                      <td className="shikyuu-border-right shikyuu-special-border-top shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id='p_k33'>支給合計</td>
                    </>
                  ) : (
                    // Bonus mode: 7 empty cells + 支給合計
                    <>
                      {Array.from({ length: 7 }, (_, index) => (
                        <td key={`empty-total-${index}`}></td>
                      ))}
                      <td className="shikyuu-border-right shikyuu-special-border-top shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id='p_k33'>支給合計</td>
                    </>
                  )}
                </tr>
              );
              
              const totalValueRow = (
                <tr key="payment-total-value" className="shikyuu-border-bottom">
                  <td></td>
                  {dataType === 'salary' ? (
                    // Salary mode: 5 empty cells + 課税支給額 + 非課支給額 + 支給合計 values
                    <>
                      {Array.from({ length: 5 }, (_, index) => (
                        <td key={`empty-total-value-${index}`}></td>
                      ))}
                      {taxablePaymentItem && (
                        <td className="shikyuu-border-right shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id={taxablePaymentItem.valueId}>
                          {hasSearched ? getValueForCell(taxablePaymentItem.valueId) : ''}
                        </td>
                      )}
                      {nonTaxablePaymentItem && (
                        <td className="shikyuu-border-right shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id={nonTaxablePaymentItem.valueId}>
                          {hasSearched ? getValueForCell(nonTaxablePaymentItem.valueId) : ''}
                        </td>
                      )}
                      <td className="shikyuu-border-right shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id='p_v33'>
                        {hasSearched ? getValueForCell('p_v33') : ''}
                      </td>
                    </>
                  ) : (
                    // Bonus mode: 7 empty cells + 支給合計 value
                    <>
                      {Array.from({ length: 7 }, (_, index) => (
                        <td key={`empty-total-value-${index}`}></td>
                      ))}
                      <td className="shikyuu-border-right shikyuu-special-border-bottom shikyuu-special-border-left shikyuu-special-border-right" id='p_v33'>
                        {hasSearched ? getValueForCell('p_v33') : ''}
                      </td>
                    </>
                  )}
                </tr>
              );
              
                return [
                  firstKeyRow, firstValueRow,
                  secondKeyRow, secondValueRow,
                  thirdKeyRow, thirdValueRow,
                  fourthKeyRow, fourthValueRow,
                  totalRow, totalValueRow
                ];
              } catch (e) {
                console.warn('Error rendering payment section:', e);
                return null;
              }
            })()}

            {/* 控除 (Deduction) Section */}
            {(() => {
              try {
                /**
                 * Safely get enabled deduction items array.
                 * Ensures we have a valid array even if settings are corrupted or missing.
                 */
                const deductionEnabled = safeSettings.deduction && Array.isArray(safeSettings.deduction.enabled)
                  ? safeSettings.deduction.enabled
                  : [];
                
                /**
                 * Filter selected deduction items.
                 * Only includes items that are enabled in settings.
                 */
                const selectedDeductionItems = deductionItems.filter(item =>
                  safeIncludes(deductionEnabled, item.id)
                );

              /**
               * Create a fixed structure with 6 rows (2 key-value pairs + 2 total rows).
               * 
               * Deduction section structure:
               * - Row 1: Key labels (items 1-9)
               * - Row 2: Values (items 1-9)
               * - Row 3: Key labels (items 10-18)
               * - Row 4: Values (items 10-18)
               * - Row 5: Total row key (控除合計)
               * - Row 6: Total row value (控除合計)
               */
              const rows = [];

              // First row (items 1-9)
              const firstRowItems = selectedDeductionItems.slice(0, 9);
              const firstKeyRow = (
                <tr key="deduction-key-1" className="koujo-border-top">
                  <td rowSpan={6} className="vertical-text koujo-border-left">控除</td>
                  {firstRowItems.map((item, index) => (
                    <td
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "koujo-border-left" : index === 8 ? "koujo-border-right no-left-border" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {Array.from({ length: 9 - firstRowItems.length }, (_, index) => (
                    <td key={`empty-key-1-${index}`} className={index === (9 - firstRowItems.length - 1) ? "koujo-border-right" : ""}></td>
                  ))}
                </tr>
              );

              const firstValueRow = (
                <tr key="deduction-value-1">
                  {firstRowItems.map((item, index) => (
                    <td
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "koujo-border-left" : index === 8 ? "koujo-border-right no-left-border" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {Array.from({ length: 9 - firstRowItems.length }, (_, index) => (
                    <td key={`empty-value-1-${index}`} className={index === (9 - firstRowItems.length - 1) ? "koujo-border-right" : ""}></td>
                  ))}
                </tr>
              );

              // Second row (items 10-18)
              const secondRowItems = selectedDeductionItems.slice(9, 18);
              const secondKeyRow = (
                <tr key="deduction-key-2">
                  {secondRowItems.map((item, index) => (
                    <td
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "koujo-border-left" : index === 8 ? "koujo-border-right no-left-border" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {Array.from({ length: 9 - secondRowItems.length }, (_, index) => (
                    <td key={`empty-key-2-${index}`} className={index === (9 - secondRowItems.length - 1) ? "koujo-border-right" : ""}></td>
                  ))}
                </tr>
              );

              const secondValueRow = (
                <tr key="deduction-value-2">
                  {secondRowItems.map((item, index) => (
                    <td
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "koujo-border-left" : index === 8 ? "koujo-border-right no-left-border" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {Array.from({ length: 9 - secondRowItems.length }, (_, index) => (
                    <td key={`empty-value-2-${index}`} className={index === (9 - secondRowItems.length - 1) ? "koujo-border-right" : ""}></td>
                  ))}
                </tr>
              );

              /**
               * Total rows - put total deduction (控除合計) in the last column.
               * Displays the total deduction amount with special border styling.
               */
              const totalRow = (
                <tr key="deduction-total" >
                  <td className="koujo-border-left"></td>
                  {Array.from({ length: 7 }, (_, index) => (
                    <td key={`empty-total-${index}`}></td>
                  ))}
                  <td className="koujo-border-right koujo-special-border-top  koujo-special-border-left koujo-special-border-right no-left-border" id='d_k15'>控除合計</td>
                </tr>
              );

              const totalValueRow = (
                <tr key="deduction-total-value" className="koujo-border-bottom">
                  <td className="koujo-border-left"></td>
                  {Array.from({ length: 7 }, (_, index) => (
                    <td key={`empty-total-value-${index}`}></td>
                  ))}
                  <td className="koujo-border-right koujo-special-border-bottom koujo-special-border-left koujo-special-border-right no-left-border" id='d_v15'>
                    {hasSearched ? getValueForCell('d_v15') : ''}
                  </td>
                </tr>
              );

               return [
                 firstKeyRow, firstValueRow,
                 secondKeyRow, secondValueRow,
                 totalRow, totalValueRow
               ];
              } catch (e) {
                console.warn('Error rendering deduction section:', e);
                return null;
              }
            })()}

            {/* 勤怠 (Attendance) Section */}
            {(() => {
              try {
                /**
                 * Safely get enabled attendance items array.
                 * Ensures we have a valid array even if settings are corrupted or missing.
                 */
                const attendanceEnabled = safeSettings.attendance && Array.isArray(safeSettings.attendance.enabled)
                  ? safeSettings.attendance.enabled
                  : [];
                
                /**
                 * Filter selected attendance items.
                 * Only includes items that are enabled in settings.
                 */
                const selectedAttendanceItems = attendanceItems.filter(item =>
                  safeIncludes(attendanceEnabled, item.id)
                );

              /**
               * For bonus payslip, create dynamic structure based on available items.
               * 
               * Bonus attendance section is dynamic:
               * - Calculates number of rows needed (9 items per row)
               * - Creates key-value pairs for each row
               * - RowSpan is calculated dynamically
               */
              if (dataType === 'bonus') {
                /**
                 * Calculate how many rows we need (1 row per 9 items, minimum 1 row).
                 * Each row can display 9 attendance items (key row + value row = 2 rows per item set).
                 */
                const totalRows = Math.max(1, Math.ceil(selectedAttendanceItems.length / 9));
                const rows = [];
                
                /**
                 * Calculate total rowSpan for the "勤怠" vertical label cell.
                 * RowSpan includes: attendance rows (key + value pairs).
                 */
                const totalRowSpan = totalRows * 2;
                
                for (let i = 0; i < totalRows; i++) {
                  const startIndex = i * 9;
                  const endIndex = startIndex + 9;
                  const rowItems = selectedAttendanceItems.slice(startIndex, endIndex);
                  
                  if (rowItems.length === 0) break; // Don't create empty rows
                  
                  const keyRow = (
                    <tr key={`attendance-key-${i + 1}`} className={i === 0 ? "kintai-border-top" : ""}>
                      {i === 0 && <td rowSpan={totalRowSpan} className="vertical-text kintai-border-left">勤怠</td>}
                      {rowItems.map((item, index) => (
                        <td
                          key={item.keyId}
                          id={item.keyId}
                          className={index === 0 ? "kintai-border-left" : index === rowItems.length - 1 ? "kintai-border-right" : ""}
                        >
                          {item.name}
                        </td>
                      ))}
                      {Array.from({ length: 9 - rowItems.length }, (_, index) => (
                        <td key={`empty-key-${i + 1}-${index}`} className={index === (9 - rowItems.length - 1) ? "kintai-border-right" : ""}>&nbsp;</td>
                      ))}
                    </tr>
                  );
                  
                  const valueRow = (
                    <tr key={`attendance-value-${i + 1}`} className={i === totalRows - 1 ? "kintai-border-bottom" : ""}>
                      {rowItems.map((item, index) => (
                        <td
                          key={item.valueId}
                          id={item.valueId}
                          className={index === 0 ? "kintai-border-left" : index === rowItems.length - 1 ? "kintai-border-right" : ""}
                        >
                          {hasSearched ? getValueForCell(item.valueId) : ''}
                        </td>
                      ))}
                      {Array.from({ length: 9 - rowItems.length }, (_, index) => (
                        <td key={`empty-value-${i + 1}-${index}`} className={index === (9 - rowItems.length - 1) ? "kintai-border-right" : ""}>&nbsp;</td>
                      ))}
                    </tr>
                  );
                  
                  rows.push(keyRow, valueRow);
                }
                
                return rows;
              }

              /**
               * For salary payslip, use the original fixed structure.
               * 
               * Salary attendance section has a fixed structure with 6 rows:
               * - Row 1: Key labels (items 1-9)
               * - Row 2: Values (items 1-9)
               * - Row 3: Key labels (items 10-18)
               * - Row 4: Values (items 10-18)
               * - Row 5: Key labels (items 19-22)
               * - Row 6: Values (items 19-22)
               */
              const rows = [];

              // First row (items 1-9)
              const firstRowItems = selectedAttendanceItems.slice(0, 9);
              const firstKeyRow = (
                <tr key="attendance-key-1" className="kintai-border-top">
                  <td rowSpan={6} className="vertical-text kintai-border-left">勤怠</td>
                  {firstRowItems.map((item, index) => (
                    <td
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "kintai-border-left" : index === 8 ? "kintai-border-right" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {Array.from({ length: 9 - firstRowItems.length }, (_, index) => (
                    <td key={`empty-key-1-${index}`} className={index === (9 - firstRowItems.length - 1) ? "kintai-border-right" : ""}></td>
                  ))}
                </tr>
              );

              const firstValueRow = (
                <tr key="attendance-value-1">
                  {firstRowItems.map((item, index) => (
                    <td
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "kintai-border-left" : index === 8 ? "kintai-border-right" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {Array.from({ length: 9 - firstRowItems.length }, (_, index) => (
                    <td key={`empty-value-1-${index}`} className={index === (9 - firstRowItems.length - 1) ? "kintai-border-right" : ""}></td>
                  ))}
                </tr>
              );

              // Second row (items 10-18)
              const secondRowItems = selectedAttendanceItems.slice(9, 18);
              const secondKeyRow = (
                <tr key="attendance-key-2">
                  {secondRowItems.map((item, index) => (
                    <td
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "kintai-border-left" : index === 8 ? "kintai-border-right" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {Array.from({ length: 9 - secondRowItems.length }, (_, index) => (
                    <td key={`empty-key-2-${index}`} className={index === (9 - secondRowItems.length - 1) ? "kintai-border-right" : ""}></td>
                  ))}
                </tr>
              );

              const secondValueRow = (
                <tr key="attendance-value-2">
                  {secondRowItems.map((item, index) => (
                    <td
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "kintai-border-left" : index === 8 ? "kintai-border-right" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {Array.from({ length: 9 - secondRowItems.length }, (_, index) => (
                    <td key={`empty-value-2-${index}`} className={index === (9 - secondRowItems.length - 1) ? "kintai-border-right" : ""}></td>
                  ))}
                </tr>
              );

              // Third row (items 19-22)
              const thirdRowItems = selectedAttendanceItems.slice(18, 22);
              const thirdKeyRow = (
                <tr key="attendance-key-3" >
                  {thirdRowItems.map((item, index) => (
                    <td
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "kintai-border-left" : index === 8 ? "kintai-border-right" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {Array.from({ length: 9 - thirdRowItems.length }, (_, index) => (
                    <td key={`empty-key-3-${index}`} className={index === (9 - thirdRowItems.length - 1) ? "kintai-border-right" : ""}></td>
                  ))}
                </tr>
              );

              const thirdValueRow = (
                <tr key="attendance-value-3" className="kintai-border-bottom">
                  {thirdRowItems.map((item, index) => (
                    <td
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "kintai-border-left" : index === 8 ? "kintai-border-right" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {Array.from({ length: 9 - thirdRowItems.length }, (_, index) => (
                    <td key={`empty-value-3-${index}`} className={index === (9 - thirdRowItems.length - 1) ? "kintai-border-right" : ""}></td>
                  ))}
                </tr>
              );

               return [
                 firstKeyRow, firstValueRow,
                 secondKeyRow, secondValueRow,
                 thirdKeyRow, thirdValueRow
               ];
              } catch (e) {
                console.warn('Error rendering attendance section:', e);
                return null;
              }
            })()}

            {/* 差引 (Deduction Amount) Section for Bonus */}
            {dataType === 'bonus' && (() => {
              try {
                /**
                 * Safely get enabled notes items array.
                 * For bonus, notes section is labeled as "差引".
                 */
                const notesEnabled = safeSettings.notes && Array.isArray(safeSettings.notes.enabled)
                  ? safeSettings.notes.enabled
                  : [];
                
                /**
                 * Filter enabled notes items.
                 * Only includes items that are enabled in settings.
                 */
                const selectedNotesItems = notesItems.filter(item => 
                  safeIncludes(notesEnabled, item.id)
                );

              /**
               * Separate items into two groups for bonus:
               * - First 6 items: 実支給額, 振込金額１, 振込金額２, 振込金額３, 現金支給額, 現物支給額
               * - Last 2 items: 課税対象額, 差引支給額 (displayed with special border styling)
               */
              const firstSixItems = selectedNotesItems.filter(item => 
                safeIncludes(['bonus_notes_1', 'bonus_notes_2', 'bonus_notes_3', 'bonus_notes_4', 'bonus_notes_5', 'bonus_notes_6'], item.id)
              );
              const lastTwoItems = selectedNotesItems.filter(item => 
                safeIncludes(['bonus_notes_7', 'bonus_notes_8'], item.id)
              );

              // Create key row
              const notesKeyRow = (
                <tr key="notes-key" className="kiji-border-top">
                  <td rowSpan={2} className="vertical-text kiji-border-left">差引</td>
                  {/* First 6 items */}
                  {firstSixItems.map((item, index) => (
                    <td
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "kiji-border-left" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {/* Add empty cells to maintain structure with 1 empty cell in the middle */}
                  {Array.from({ length: 1 }, (_, index) => (
                    <td key={`empty-notes-${index}`}></td>
                  ))}
                  {/* Last 2 items at the end */}
                  {lastTwoItems.map((item, index) => (
                    <td
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "kiji-special-border-top kiji-special-border-left kiji-special-border-right" : "kiji-special-border-top kiji-special-border-left kiji-special-border-right"}
                    >
                      {item.name}
                    </td>
                  ))}
                  {/* Fill remaining cells if needed */}
                  {Array.from({ length: 9 - firstSixItems.length - 1 - lastTwoItems.length }, (_, index) => (
                    <td key={`empty-notes-end-${index}`}></td>
                  ))}
                </tr>
              );

              // Create value row
              const notesValueRow = (
                <tr key="notes-value" className="kiji-border-bottom">
                  {/* First 6 items */}
                  {firstSixItems.map((item, index) => (
                    <td
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "kiji-border-left" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {/* Add empty cells to maintain structure with 1 empty cell in the middle */}
                  {Array.from({ length: 1 }, (_, index) => (
                    <td key={`empty-notes-value-${index}`}></td>
                  ))}
                  {/* Last 2 items at the end */}
                  {lastTwoItems.map((item, index) => (
                    <td
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "kiji-special-border-bottom kiji-special-border-left kiji-special-border-right" : "kiji-special-border-bottom kiji-special-border-left kiji-special-border-right"}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {/* Fill remaining cells if needed */}
                  {Array.from({ length: 9 - firstSixItems.length - 1 - lastTwoItems.length }, (_, index) => (
                    <td key={`empty-notes-value-end-${index}`}></td>
                  ))}
                </tr>
              );

              return [notesKeyRow, notesValueRow];
              } catch (e) {
                console.warn('Error rendering bonus notes section:', e);
                return null;
              }
            })()}

            {/* 差引 (Deduction Amount) Section for Salary */}
            {dataType === 'salary' && (() => {
              try {
                /**
                 * Safely get enabled notes items array.
                 * For salary, notes section is labeled as "差引".
                 */
                const notesEnabled = safeSettings.notes && Array.isArray(safeSettings.notes.enabled)
                  ? safeSettings.notes.enabled
                  : [];
                
                /**
                 * Filter enabled notes items.
                 * Only includes items that are enabled in settings.
                 */
                const selectedNotesItems = notesItems.filter(item => 
                  safeIncludes(notesEnabled, item.id)
                );

              /**
               * Separate items into two groups for salary:
               * - First 6 items: 実支給額, 振込金額１, 振込金額２, 振込金額３, 現金支給額, 現物支給額
               * - Last 3 items: 定額減税, 課税対象額, 差引支給額 (displayed with special border styling)
               */
              const firstSixItems = selectedNotesItems.filter(item => 
                safeIncludes(['salary_notes_1', 'salary_notes_2', 'salary_notes_3', 'salary_notes_4', 'salary_notes_5', 'salary_notes_6'], item.id)
              );
              const lastThreeItems = selectedNotesItems.filter(item => 
                safeIncludes(['salary_notes_7', 'salary_notes_8', 'salary_notes_9'], item.id)
              );

              // Create key row
              const notesKeyRow = (
                <tr key="notes-key" className="kiji-border-top">
                  <td rowSpan={2} className="vertical-text kiji-border-left">差引</td>
                  {/* First 6 items */}
                  {firstSixItems.map((item, index) => (
                    <td
                      key={item.keyId}
                      id={item.keyId}
                      className={index === 0 ? "kiji-border-left" : ""}
                    >
                      {item.name}
                    </td>
                  ))}
                  {/* Last 3 items at the end */}
                  {lastThreeItems.map((item, index) => (
                    <td
                      key={item.keyId}
                      id={item.keyId}
                      className="kiji-special-border-top kiji-special-border-left kiji-special-border-right"
                    >
                      {item.name}
                    </td>
                  ))}
                  {/* Fill remaining cells if needed */}
                  {Array.from({ length: 9 - firstSixItems.length - lastThreeItems.length }, (_, index) => (
                    <td key={`empty-notes-end-${index}`}></td>
                  ))}
                </tr>
              );

              // Create value row
              const notesValueRow = (
                <tr key="notes-value" className="kiji-border-bottom">
                  {/* First 6 items */}
                  {firstSixItems.map((item, index) => (
                    <td
                      key={item.valueId}
                      id={item.valueId}
                      className={index === 0 ? "kiji-border-left" : ""}
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {/* Last 3 items at the end */}
                  {lastThreeItems.map((item, index) => (
                    <td
                      key={item.valueId}
                      id={item.valueId}
                      className="kiji-special-border-bottom kiji-special-border-left kiji-special-border-right"
                    >
                      {hasSearched ? getValueForCell(item.valueId) : ''}
                    </td>
                  ))}
                  {/* Fill remaining cells if needed */}
                  {Array.from({ length: 9 - firstSixItems.length - lastThreeItems.length }, (_, index) => (
                    <td key={`empty-notes-value-end-${index}`}></td>
                  ))}
                </tr>
              );

              return [notesKeyRow, notesValueRow];
              } catch (e) {
                console.warn('Error rendering salary notes section:', e);
                return null;
              }
            })()}

            {/* Company Name Row - Only shown if showCompanyName is enabled */}
            {safeSettings.showCompanyName && (
              <tr className="all-white-row">
                <td colSpan={10} className="company-name no-border">株式会社西行土木</td>
              </tr>
            )}

            {/* メモ (Memo) Section - Simple 3 rows with shared border */}
            <tr className="memo-row-first">
              {/* Vertical label cell - spans 3 rows */}
              <td rowSpan={3} className="vertical-text memo-border-top memo-border-left memo-border-right memo-border-bottom">
                {safeSettings.showMemo ? "メモ" : ""}
              </td>
              {/* Memo 1 cell - personal message 1 (個人メッセージ1) */}
              <td colSpan={6} className={`memo-cell memo-top-border ${!safeSettings.showMemo ? 'hidden-memo' : ''}`} id='memo1'>
                {safeSettings.showMemo ? (tableData && tableData.memo1 ? tableData.memo1 : '') : ""}
              </td>
              {/* Cumulative totals labels */}
              <td className="no-border">課税支給累計</td>
              <td className="no-border">社保控除累計</td>
              <td className="no-border">所得税累計</td>
            </tr>
            <tr className="all-white-row memo-row-middle">
              {/* Memo 2 cell - personal message 2 (個人メッセージ2) */}
              <td colSpan={6} className={`memo-cell ${!safeSettings.showMemo ? 'hidden-memo' : ''}`} id='memo2'>
                {safeSettings.showMemo ? (tableData && tableData.memo2 ? tableData.memo2 : '') : ""}
              </td>
              {/* Cumulative totals values */}
              <td className="no-border" id='p_v32'>
                {hasSearched ? getValueForCell('p_v32') : ''} {/* 課税支給累計 value */}
              </td>
              <td className="no-border" id='d_v13'>
                {hasSearched ? getValueForCell('d_v13') : ''} {/* 社保控除累計 value */}
              </td>
              <td className="no-border" id='d_v14'>
                {hasSearched ? getValueForCell('d_v14') : ''} {/* 所得税累計 value */}
              </td>
            </tr>
            {/* Empty bottom row for memo section */}
            <tr className="all-white-row memo-row-last">
              <td colSpan={6} className={`memo-cell memo-bottom-border ${!safeSettings.showMemo ? 'hidden-memo' : ''}`}>&nbsp;</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}; 